import { isObjectAssetPlacementActive, handlePlacementClick } from '../objects/object_asset_system.js?v=20260903c';
import { planSurfaceMaxScale } from '../render/plan_surface_runtime.mjs';
import * as AppController from '../app/app_controller.js?v=20260904f';
import * as SelectionController from '../selection/selection_controller.js?v=20260904f';
import * as ViewportController from '../viewport/viewport_controller.js?v=20260706d';
import * as FloorplanController from '../viewport/floorplan_controller.js?v=20260903c';
import * as EditCommandController from './edit_command_controller.js?v=20260903c';
import * as ClipboardController from '../selection/clipboard_controller.js?v=20260904f';
import { createPointerMoveScheduler } from './pointer_move_scheduler.js';
import {
  activeTool,
  handlePointerDown as handleToolPointerDown,
  handlePointerMove as handleToolPointerMove,
  handlePointerUp as handleToolPointerUp
} from './tool_service.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { GLOBAL_MENU_ACTIONS } from '../app/global_menu_actions.js?v=20260904f';
import { globalMenuIconContentHtml } from '../app/global_menu_icon_registry.js?v=20260820d';
import {
  contextMenuGlobalEntries,
  contextMenuSystemEntriesForSystem
} from '../app/context_menu_service.js?v=20260723a';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import { clamp } from '../utils/common_constants.js?v=20260706d';
import {
  appPerfEnd,
  appPerfStep,
  appPerfTimer,
  appCancelAnimationFrame,
  appRequestAnimationFrame
} from '../render/render_scheduler.js?v=20260706e';
import {
  clearAllInteractionFlagsSoon,
  clearAllInteractionTimers,
  clearInteractionFlag,
  getInteractionFlag,
  setInteractionFlag
} from './interaction_transient_state.js?v=20260904f';
import {
  screenToWorld,
  setStatus
} from '../render/plan_renderer.js?v=20260904f';
import {
  clearDimensionSelection,
  isDimensionToolActive,
  handleDimensionPointerDown,
  handleDimensionPointerMove,
  handleDimensionPointerUp,
  wantsDimensionPointerMoveFrame
} from '../services/dimension_service.js?v=20260904f';
import {
  clearTextAnnotationSelection,
  handleTextAnnotationDoubleClick,
  handleTextAnnotationPointerDown,
  handleTextAnnotationPointerMove,
  handleTextAnnotationPointerUp,
  wantsTextAnnotationPointerMoveFrame
} from '../services/text_annotation_service.js?v=20260903c';
import {
  clearAreaMeasurementSelection,
  handleAreaMeasurementPointerDown,
  handleAreaMeasurementPointerMove,
  handleAreaMeasurementPointerUp,
  wantsAreaMeasurementPointerMoveFrame
} from '../services/area_measurement_service.js?v=20260903c';
import {
  handleAutoAreaMeasurementPointerDown
} from '../services/auto_area_measurement_service.js?v=20260814c';
import {
  clearObstacleSelection,
  handleGlobalObstacleLengthInputKey,
  handleGlobalObstacleDrawClick,
  hitTestObstacle,
  isGlobalObstacleDrawToolActive,
  obstacleActiveTool,
  selectObstacle
} from '../obstacles/obstacles.js?v=20260903c';
import { S } from '../app/app_state.js?v=20260817c';
import {
  canRedo,
  canUndo,
  recordUndo
} from '../services/history_service.js?v=20260903c';

const PLAN_MIN_SCALE = 0.01;
const PLAN_MAX_SCALE = 10;
const MIDDLE_BUTTON_DOUBLE_CLICK_MS = 360;
const PLAN_CONTEXT_MENU_MARGIN = 8;

function wheelZoomFactor(event) {
    const lineHeightPx = 16;
    const pageHeightPx = 800;
    const modeScale = event.deltaMode === 1
      ? lineHeightPx
      : event.deltaMode === 2
        ? pageHeightPx
        : 1;
    const normalizedDelta = clamp(event.deltaY * modeScale, -180, 180);
    return Math.exp(-normalizedDelta * 0.0011);
}

function preferredHitSystemFromTarget(target) {
    return SystemRegistry.preferredHitSystemFromTarget(target) || null;
}

function hitTestActiveDrawingSystem(clientX, clientY, targetOrPreferredSystem = null) {
    return SystemRegistry.hitTestPlan(clientX, clientY, {
      target: typeof targetOrPreferredSystem === 'string' ? null : targetOrPreferredSystem,
      preferredSystemId: typeof targetOrPreferredSystem === 'string' ? targetOrPreferredSystem : null
    });
}

// Returns the one system id that owns the current resolved multi-selection,
// or null if it's empty, spans more than one system, or includes obstacles
// (a core concept, not owned by any product system). Used to gate
// plan_renderer.js's active-system-only render fast path -- see its caller.
function singleSystemIdForMultiSelectionDrag() {
    const selection = SelectionController.resolvedMultiSelection();
    if (Array.isArray(selection.obstacleIds) && selection.obstacleIds.length) return null;
    let matchedSystemId = null;
    for (const system of SystemRegistry.allSystems()) {
      const fields = system.multiSelectionFields || [];
      const hasEntries = fields.some(field => Array.isArray(selection[field]) && selection[field].length > 0);
      if (!hasEntries) continue;
      if (matchedSystemId && matchedSystemId !== system.id) return null;
      matchedSystemId = system.id;
    }
    if (!matchedSystemId || S.drag?.startObstacles?.length) return null;
    const snapshots = S.drag?.systemSnapshots;
    if (!Array.isArray(snapshots) || !snapshots.length) return null;
    return snapshots.every(snapshot => snapshot.systemId === matchedSystemId)
      ? matchedSystemId
      : null;
}

function planContextMenuItemHtml(actionCode, label, iconKey, scope) {
    const icon = iconKey
      ? `<span class="plan-context-menu__icon icon-btn ${escapeHtml(iconKey)}-icon" aria-hidden="true">${globalMenuIconContentHtml(iconKey)}</span>`
      : '<span class="plan-context-menu__icon is-placeholder" aria-hidden="true"></span>';
    return `<button class="plan-context-menu__item" type="button" role="menuitem" data-plan-context-action="${escapeHtml(actionCode)}" data-plan-context-scope="${scope}">${icon}<span class="plan-context-menu__label">${escapeHtml(label)}</span></button>`;
}

// Rebuilt on every showPlanContextMenu() call, not just once — the active
// system (and therefore its toolbar entries below the thick separator) can
// have changed between two right-clicks.
function renderPlanContextMenuContent(menu) {
    const parts = contextMenuGlobalEntries().map(entry => (
      entry.entry_type === 'separator'
        ? '<div class="plan-context-menu__separator" role="separator"></div>'
        : planContextMenuItemHtml(entry.action_code, entry.label, entry.icon_key, 'global')
    ));
    const system = SystemRegistry.activeSystem?.();
    if (system) {
      const resolvedSystemButtons = contextMenuSystemEntriesForSystem(system.id)
        .map(entry => (system.toolbarButtons || []).find(button => button.id === entry.button_code))
        .filter(button => button && button.controlType !== 'input');
      if (resolvedSystemButtons.length) {
        parts.push('<div class="plan-context-menu__separator plan-context-menu__separator--thick" role="separator"></div>');
        resolvedSystemButtons.forEach(button => {
          parts.push(planContextMenuItemHtml(button.action, button.label, null, 'system'));
        });
      }
    }
    menu.innerHTML = parts.join('');
}

function executePlanContextMenuAction(action, scope) {
    if (scope === 'system') {
      return SystemRegistry.handleToolbarAction(action) === true;
    }
    const entry = GLOBAL_MENU_ACTIONS[action];
    if (entry?.kind === 'button' && typeof entry.onClick === 'function') {
      entry.onClick();
      return true;
    }
    return false;
}

function hasAppClipboardPayload() {
    return Boolean(S.clipboard?.payload);
}

function isSystemPlacementToolActive() {
    const tool = activeTool();
    return Boolean(tool && tool.owner && tool.owner !== 'core');
}

function syncPlanContextMenuState(menu = ensurePlanContextMenu()) {
    const enabledByAction = {
      'undo': canUndo(),
      'redo': canRedo(),
      'context-copy': SelectionController.hasGlobalSelection(),
      'context-paste': hasAppClipboardPayload()
    };
    menu.querySelectorAll('[data-plan-context-action]').forEach(item => {
      const action = item.getAttribute('data-plan-context-action') || '';
      const isEnabled = enabledByAction[action] !== false;
      item.disabled = !isEnabled;
      item.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
    });
}

function ensurePlanContextMenu() {
    let menu = document.getElementById('planContextMenu');
    if (menu) return menu;
    menu = document.createElement('div');
    menu.id = 'planContextMenu';
    menu.className = 'plan-context-menu';
    menu.setAttribute('role', 'menu');
    menu.addEventListener('click', event => {
      const item = event.target?.closest?.('[data-plan-context-action]');
      if (!item) return;
      event.preventDefault();
      if (item.disabled || item.getAttribute('aria-disabled') === 'true') return;
      const action = item.getAttribute('data-plan-context-action') || '';
      const scope = item.getAttribute('data-plan-context-scope') || 'global';
      hidePlanContextMenu();
      executePlanContextMenuAction(action, scope);
    });
    document.body.appendChild(menu);
    return menu;
}

function hidePlanContextMenu() {
    const menu = document.getElementById('planContextMenu');
    if (!menu) return false;
    if (menu.hidden) return false;
    menu.hidden = true;
    menu.classList.remove('opens-up');
    return true;
}

function showPlanContextMenu(event) {
    const menu = ensurePlanContextMenu();
    renderPlanContextMenuContent(menu);
    syncPlanContextMenuState(menu);
    menu.hidden = false;
    menu.style.left = '0px';
    menu.style.top = '0px';
    menu.classList.remove('opens-up');
    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    let left = event.clientX;
    let top = event.clientY;
    if (left + rect.width + PLAN_CONTEXT_MENU_MARGIN > viewportWidth) {
      left = Math.max(PLAN_CONTEXT_MENU_MARGIN, viewportWidth - rect.width - PLAN_CONTEXT_MENU_MARGIN);
    }
    if (top + rect.height + PLAN_CONTEXT_MENU_MARGIN > viewportHeight) {
      top = Math.max(PLAN_CONTEXT_MENU_MARGIN, event.clientY - rect.height);
      menu.classList.add('opens-up');
    }
    menu.style.left = `${Math.max(PLAN_CONTEXT_MENU_MARGIN, left)}px`;
    menu.style.top = `${Math.max(PLAN_CONTEXT_MENU_MARGIN, top)}px`;
    return true;
}

function bindSvgInteractions() {
    const svg = document.getElementById('planSvg');
    let lastMiddleMouseDownAt = 0;
    let interruptedPress = false;
    let pressContext = null;
    let lastMultiSelectionDelta = null;
    const currentPressContext = () => ({
      project: S.project,
      sheetId: S.drawingSheets?.activeSheetId,
      view: S.project?.currentView
    });
    const pressContextIsCurrent = () => !pressContext || (
      pressContext.project === S.project
      && pressContext.sheetId === S.drawingSheets?.activeSheetId
      && pressContext.view === S.project?.currentView
    );
    // Read identities and scalar tool values only. activeTool() and product
    // readiness hooks may normalize state and belong in the frame callback.
    function capturePointerContext() {
      return [
        S.project, S.project?.currentView, S.drawingSheets?.activeSheetId,
        S.drag, S.drag?.active, S.drag?.type, S.drag?.systemId, S.drag?.owner,
        SystemRegistry.state?.activeSystemId,
        S.ui?.activeTool?.owner, S.ui?.activeTool?.tool, S.ui?.obstacleTool,
        S.ui?.objectAssetPlacement, S.ui?.clipboardPastePending, S.ui?.dimensionDraft,
        S.ui?.areaMeasurementDraft, S.ui?.areaMeasurementEdit
      ];
    }
    function canPreviewDimensionAfterInterruption(event) {
      return event.buttons === 0 && !S.drag?.active
        && S.project?.currentView === 'plan' && isDimensionToolActive();
    }
    const pointerMoves = createPointerMoveScheduler({
      requestFrame: appRequestAnimationFrame,
      cancelFrame: appCancelAnimationFrame,
      captureContext: capturePointerContext,
      isCurrent: previous => capturePointerContext().every((value, index) => value === previous[index]),
      handleMove: event => {
        if (interruptedPress) {
          // Hover is a preview, not a continuation of the interrupted press.
          // Keep releases/clicks blocked until a fresh canvas press arrives.
          if (canPreviewDimensionAfterInterruption(event)) handleDimensionPointerMove(event);
          return;
        }
        if (S.drag?.active && !pressContextIsCurrent()) return;
        if (S.drag?.active && !pressContext) pressContext = currentPressContext();
        if (planMouseMoveNeedsFrame()) handlePlanMouseMove(event);
      }
    });
    // Run before a new gesture/tool action can replace its predecessor's state.
    function flushBeforePlanPress(event) {
      if (svg.contains(event.target)) {
        if (event.type === 'click' && interruptedPress) {
          event.preventDefault?.();
          event.stopImmediatePropagation?.();
          return;
        }
        if (event.type !== 'click') {
          interruptedPress = false;
          pressContext = currentPressContext();
          lastMultiSelectionDelta = null;
        }
      }
      pointerMoves.flush();
    }
    for (const type of ['pointerdown', 'mousedown', 'click']) {
      document.addEventListener(type, flushBeforePlanPress, true);
    }
    window.addEventListener('blur', finishPlanInteractionOnFocusLoss);
    window.addEventListener('pointercancel', () => pointerMoves.cancel(), true);
    window.addEventListener('keydown', event => {
      if (event.key === 'Escape') pointerMoves.cancel();
    }, true);

    document.addEventListener('pointerdown', event => {
      const menu = document.getElementById('planContextMenu');
      if (!menu || menu.hidden || menu.contains(event.target)) return;
      hidePlanContextMenu();
    }, true);
    window.addEventListener('resize', hidePlanContextMenu);
    window.addEventListener('scroll', hidePlanContextMenu, true);

    function fitAllPlanContentFromMiddleButton(event) {
      if (event?.button !== 1) return false;
      const now = Date.now();
      const isDoubleClick = now - lastMiddleMouseDownAt <= MIDDLE_BUTTON_DOUBLE_CLICK_MS;
      lastMiddleMouseDownAt = isDoubleClick ? 0 : now;
      if (!isDoubleClick) return false;
      event.preventDefault?.();
      event.stopPropagation?.();
      if (ViewportController.fitCameraToAllPlanContent()) {
        setInteractionFlag('suppressPlanClick', true);
        AppController.startPlanNavigationLod('fit-all');
        AppController.requestPlanRender('fit-all-middle-double-click', { kind: 'navigation' });
      }
      return true;
    }

    // Core object placement is independent of the navigated product owner.
    // Reserve presses before SVG objects can start their own selection/drag.
    for (const type of ['pointerdown', 'mousedown']) {
      svg.addEventListener(type, event => {
        if (event.button === 0 && !getInteractionFlag('spaceDown') && isObjectAssetPlacementActive()) {
          event.stopPropagation();
        }
      }, true);
    }
    svg.addEventListener('click', event => {
      if (!getInteractionFlag('spaceDown') && isObjectAssetPlacementActive()) {
        if (clearInteractionFlag('suppressPlanClick')) { event.stopImmediatePropagation(); return; }
        if (handlePlacementClick(event)) event.stopImmediatePropagation();
      }
    }, true);

    svg.addEventListener('click', e => {
      const profile = appPerfTimer('svg click');
      try {
        if (S.project.currentView !== 'plan') return;
        if (clearInteractionFlag('suppressPlanClick')) return;
        if (isGlobalObstacleDrawToolActive() && handleGlobalObstacleDrawClick(e)) {
          return;
        }
        appPerfStep(profile, 'global-obstacle-click');
        if (FloorplanController.handleFloorplanMeasureClick(e)) return;
        appPerfStep(profile, 'floorplan-measure');
        if (SystemRegistry.handleClickBeforeHitTest(e, { currentView: S.project.currentView, target: e.target })) return;
        appPerfStep(profile, 'placement-click');
        appPerfStep(profile, 'door-action-click');
        const obstacleHit = hitTestObstacle(e.clientX, e.clientY);
        appPerfStep(profile, 'obstacle-hit');
        if (obstacleHit) {
          selectObstacle(obstacleHit.obstacle.id, { toggle: e.ctrlKey || e.metaKey || e.shiftKey });
          AppController.requestAppRefresh(false, 'obstacle-click');
          return;
        }
        const hit = hitTestActiveDrawingSystem(e.clientX, e.clientY, e.target);
        appPerfStep(profile, 'system-hit');
        if (hit) clearObstacleSelection();
        if (
          hit?.systemId
          && SystemRegistry.handleClick(e, { currentView: S.project.currentView, systemId: hit.systemId, hit })
        ) {
          return;
        }
        appPerfStep(profile, 'system-click');
        if (!hit) {
          clearDimensionSelection();
          clearAreaMeasurementSelection();
          clearTextAnnotationSelection();
          const activeSystemId = SystemRegistry.activeSystem()?.id || null;
          if (
            activeSystemId
            && SystemRegistry.handleClick(e, { currentView: S.project.currentView, systemId: activeSystemId, hit: null })
          ) return;
          SelectionController.clearSelectionForActiveSystem();
        }
        appPerfStep(profile, 'selection');
        AppController.requestAppRefresh(false, 'plan-click');
      } finally {
        appPerfEnd(profile);
      }
    });

    svg.addEventListener('dblclick', e => {
      if (S.project.currentView !== 'plan') return;
      if (EditCommandController.isTypingTarget(e.target)) return;
      if (handleTextAnnotationDoubleClick(e)) return;
      if (e.button === 1) {
        e.preventDefault?.();
        if (ViewportController.fitCameraToAllPlanContent()) {
          AppController.startPlanNavigationLod('fit-all');
          AppController.requestPlanRender('fit-all-middle-dblclick', { kind: 'navigation' });
        }
        return;
      }
      const hit = hitTestActiveDrawingSystem(e.clientX, e.clientY, e.target);
      if (!hit) return;
      SystemRegistry.handleDoubleClick(e, {
        currentView: S.project.currentView,
        systemId: hit.systemId,
        hit
      });
    });

    let coreOverlayPointerDownSuppression = null;
    let capturedPointerId;

    function setSvgPointerCapture(pointerId) {
      try {
        if (typeof pointerId !== 'undefined') {
          svg.setPointerCapture?.(pointerId);
          capturedPointerId = pointerId;
        }
      } catch (_err) {
        // Pointer-Capture ist nur eine Komfortabsicherung fuer Griff-Drags.
      }
    }

    function releaseSvgPointerCapture(pointerId) {
      try {
        if (typeof pointerId !== 'undefined') svg.releasePointerCapture?.(pointerId);
      } catch (_err) {
        // Der Pointer kann beim Loslassen bereits vom Browser freigegeben sein.
      } finally {
        if (pointerId === capturedPointerId) capturedPointerId = undefined;
      }
    }

    svg.addEventListener('pointerdown', e => {
      if (S.project.currentView !== 'plan') return;
      if (S.floorplanMeasure?.active) return;
      if (ClipboardController.handleClipboardPastePointerDown(e, { currentView: S.project.currentView })) {
        coreOverlayPointerDownSuppression = {
          time: Date.now()
        };
        setInteractionFlag('suppressPlanClick', true);
        return;
      }
      if (isSystemPlacementToolActive()) {
        if (e.target?.closest?.('[data-text-annotation-id], [data-text-annotation-editor]')) {
          e.preventDefault?.();
        }
        return;
      }
      if (handleTextAnnotationPointerDown(e)) {
        if (S.drag?.active && S.drag.type === 'textAnnotation') setSvgPointerCapture(e.pointerId);
        coreOverlayPointerDownSuppression = {
          time: Date.now()
        };
        setInteractionFlag('suppressPlanClick', true);
        return;
      }
      if (handleDimensionPointerDown(e)) {
        if (S.drag?.active && S.drag.type === 'dimension') setSvgPointerCapture(e.pointerId);
        coreOverlayPointerDownSuppression = {
          time: Date.now()
        };
        setInteractionFlag('suppressPlanClick', true);
        return;
      }
      if (handleAutoAreaMeasurementPointerDown(e)) {
        coreOverlayPointerDownSuppression = {
          time: Date.now()
        };
        setInteractionFlag('suppressPlanClick', true);
        return;
      }
      if (handleAreaMeasurementPointerDown(e)) {
        if (S.drag?.active && S.drag.type === 'areaMeasurementVertex') setSvgPointerCapture(e.pointerId);
        coreOverlayPointerDownSuppression = {
          time: Date.now()
        };
        setInteractionFlag('suppressPlanClick', true);
      }
    });

    svg.addEventListener('mousedown', e => {
      const profile = appPerfTimer('svg mousedown');
      try {
        if (S.project.currentView !== 'plan') return;
        if (S.floorplanMeasure?.active) return;
        if (e.button === 2) {
          e.preventDefault();
          return;
        }
        if (
          e.button === 0
          && coreOverlayPointerDownSuppression
          && Date.now() - coreOverlayPointerDownSuppression.time < 800
        ) {
          coreOverlayPointerDownSuppression = null;
          return;
        }
        coreOverlayPointerDownSuppression = null;
        ViewportController.releaseCanvasKeyboardFocus();
        appPerfStep(profile, 'focus');
        if (fitAllPlanContentFromMiddleButton(e)) return;
        if (e.button === 1 && isDimensionToolActive()) {
          ViewportController.startPan(e);
          return;
        }
        if (ClipboardController.handleClipboardPastePointerDown(e, { currentView: S.project.currentView })) return;
        appPerfStep(profile, 'clipboard-paste');
        if (isSystemPlacementToolActive()) {
          if (SystemRegistry.handlePointerDownBeforeSelection(e, { currentView: S.project.currentView, target: e.target, hit: null })) {
            e.preventDefault?.();
            setInteractionFlag('suppressPlanClick', true);
            return;
          }
          if (handleToolPointerDown(e, { currentView: S.project.currentView })) {
            e.preventDefault?.();
            setInteractionFlag('suppressPlanClick', true);
            return;
          }
          if (e.button === 0 && !getInteractionFlag('spaceDown')) {
            e.preventDefault?.();
            setInteractionFlag('suppressPlanClick', true);
            return;
          }
        }
        appPerfStep(profile, 'placement-tool-pointerdown');
        if (handleTextAnnotationPointerDown(e)) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        appPerfStep(profile, 'text-annotation-pointerdown');
        if (handleDimensionPointerDown(e)) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        appPerfStep(profile, 'dimension-pointerdown');
        if (handleAutoAreaMeasurementPointerDown(e)) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        appPerfStep(profile, 'auto-area-measurement-pointerdown');
        if (handleAreaMeasurementPointerDown(e)) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        appPerfStep(profile, 'area-measurement-pointerdown');
        if (SystemRegistry.shouldSuppressPointerDownTarget(e, { currentView: S.project.currentView, target: e.target })) {
          e.preventDefault();
          return;
        }
        appPerfStep(profile, 'direct-targets');
        if (SystemRegistry.handlePointerDownBeforeSelection(e, { currentView: S.project.currentView, target: e.target, hit: null })) return;
        appPerfStep(profile, 'placement-pointerdown');
        if (handleToolPointerDown(e, { currentView: S.project.currentView })) {
          return;
        }
        appPerfStep(profile, 'tool-pointerdown');
        const hit = hitTestActiveDrawingSystem(e.clientX, e.clientY, e.target);
        appPerfStep(profile, 'system-hit');
        if (hit) clearObstacleSelection();
        if ((e.ctrlKey || e.metaKey) && hit) {
          e.preventDefault?.();
          e.stopPropagation?.();
          if (SelectionController.toggleHitInMultiSelection(hit)) {
            setInteractionFlag('suppressPlanClick', true);
            AppController.requestAppRefresh(false, 'multi-selection-toggle');
            return;
          }
          if (
            hit.systemId
            && SystemRegistry.handleClick(e, { currentView: S.project.currentView, systemId: hit.systemId, hit })
          ) {
            setInteractionFlag('suppressPlanClick', true);
          }
          return;
        }
        if (e.button === 1 || e.button === 2 || e.altKey || e.shiftKey || (e.ctrlKey || e.metaKey) && !hit || e.target === svg && e.buttons === 1 && getInteractionFlag('spaceDown')) {
          ViewportController.startPan(e);
          return;
        }
        if (FloorplanController.startFloorplanDrag(e)) return;
        if (SystemRegistry.handlePointerDownBeforeSelection(e, { currentView: S.project.currentView, target: e.target, hit })) return;
        if (hit && SelectionController.multiSelectionContainsHit(hit)) {
          recordUndo('Auswahl verschieben');
          SelectionController.startMultiSelectionDrag(e);
          // Dragging an ALREADY-selected object (the common "click to select,
          // then drag" gesture) goes through this generic multi-selection
          // path even for a single selected item -- resolvedMultiSelection()
          // folds every system's legacy single-selection field in here too.
          // Tag the drag with its owning system, but only when the resolved
          // selection sits entirely within ONE system (no obstacles, no
          // cross-system multi-select): plan_renderer.js's active-system-only
          // fast path can then safely reuse every OTHER system's cached
          // render instead of rebuilding the whole plan every drag frame.
          if (S.drag?.active === true) {
            S.drag.systemId = singleSystemIdForMultiSelectionDrag();
          }
          return;
        }
        if (
          hit?.systemId
          && SystemRegistry.handlePointerDown(e, { currentView: S.project.currentView, systemId: hit.systemId, hit })
        ) {
          // Tag which system this drag belongs to so renderPlanOnly() can take
          // the active-system-only fast path (plan_renderer.js) instead of a
          // full rebuild on every frame -- see AGENTS.md system-independence
          // rule: a system's own drag can only ever mutate that system's own
          // project sub-state, never another system's rendered content.
          if (S.drag?.active === true) S.drag.systemId = hit.systemId;
          return;
        }
        appPerfStep(profile, 'system-pointerdown');
        if (e.button === 0) SelectionController.startPlanBoxSelection(e);
      } finally {
        appPerfEnd(profile);
      }
    });

    function planMouseMoveNeedsFrame() {
      if (S.project.currentView !== 'plan') return false;
      if (S.drag?.active) return true;
      if (isObjectAssetPlacementActive()) return true;
      if (wantsTextAnnotationPointerMoveFrame()) return true;
      if (wantsDimensionPointerMoveFrame()) return true;
      if (wantsAreaMeasurementPointerMoveFrame()) return true;
      if (ClipboardController.currentClipboardPastePending()) return true;
      if (obstacleActiveTool()) return true;
      const active = activeTool();
      if (active && active.owner !== 'core') return true;
      return SystemRegistry.wantsPointerMoveFrame({ currentView: S.project.currentView }) === true;
    }

    function handlePlanMouseMove(e) {
      const profile = appPerfTimer('svg mousemove');
      try {
        if (S.project.currentView !== 'plan') return;
        if (S.drag?.active && S.drag.type === 'pan') {
          e.preventDefault();
          const numericStartScale = Number(S.drag.startScale);
          const scale = Number.isFinite(numericStartScale) && numericStartScale > 0
            ? numericStartScale
            : S.camera2D.scale;
          S.camera2D.x = S.drag.camStart.x - (e.clientX - S.drag.startScreen.x) / scale;
          S.camera2D.z = S.drag.camStart.z - (e.clientY - S.drag.startScreen.y) / scale;
          AppController.startPlanNavigationLod('pan');
          AppController.requestPlanRender('pan', { kind: 'navigation' });
          return;
        }
        if (ClipboardController.updateClipboardPastePreview(e)) {
          appPerfStep(profile, 'clipboard-paste-preview');
          return;
        }
        if (handleTextAnnotationPointerMove(e)) {
          appPerfStep(profile, 'text-annotation-pointermove');
          return;
        }
        if (handleDimensionPointerMove(e)) {
          appPerfStep(profile, 'dimension-pointermove');
          return;
        }
        if (handleAreaMeasurementPointerMove(e)) {
          appPerfStep(profile, 'area-measurement-pointermove');
          return;
        }
        if (handleToolPointerMove(e, { currentView: S.project.currentView })) {
          appPerfStep(profile, 'tool-pointermove');
          return;
        }
        appPerfStep(profile, 'tool-pointermove');
        if (SystemRegistry.clearPointerPreview({ currentView: S.project.currentView }) === true) {
          AppController.requestPlanRender('pointer-preview-clear');
        }
        if (!S.drag?.active) return;
        const rect = svg.getBoundingClientRect();
        const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
        appPerfStep(profile, 'world');
        if (S.drag.type === 'floorplan') {
          FloorplanController.handleFloorplanDragMove(world);
          return;
        }
        if (S.drag.type === 'planBoxSelect') {
          S.drag.currentMouse = world;
          S.drag.currentScreen = { x: e.clientX, y: e.clientY };
          AppController.requestPlanRender('box-select');
        }
        if (S.drag.type === 'multiSelection') {
          const dx = world.x - S.drag.startMouse.x;
          const dz = world.z - S.drag.startMouse.z;
          if (Math.hypot(dx, dz) > 0.001) S.drag.hasMoved = true;
          S.drag.lastDx = dx;
          S.drag.lastDz = dz;
          const previewApplied = SelectionController.applyMultiSelectionDragPreview(S.drag, dx, dz);
          S.drag.previewOnlyDrag = Boolean(previewApplied);
          if (!previewApplied) {
            SelectionController.applyMultiSelectionMoveDelta(S.drag, dx, dz);
            AppController.requestPlanRender('multi-selection-drag', S.drag.systemId
              ? { kind: 'system-drag', systemId: S.drag.systemId }
              : { kind: 'full' });
          }
          // Keep raw input as well as the snapshot's snapped delta. Systems
          // can use both, so feeding rounded values back would change a snap.
          lastMultiSelectionDelta = { drag: S.drag, dx, dz };
        }
      } finally {
        appPerfEnd(profile);
      }
    }

    window.addEventListener('mousemove', e => {
      if (interruptedPress && !canPreviewDimensionAfterInterruption(e)) return;
      if (S.project?.currentView !== 'plan') {
        pointerMoves.cancel();
        return;
      }
      if (S.drag?.active && S.drag.type === 'pan') e.preventDefault?.();
      pointerMoves.queue(e);
    });

    window.addEventListener('pointermove', e => {
      if (interruptedPress) return;
      if (!(S.drag?.active && ['dimension', 'textAnnotation', 'areaMeasurementVertex'].includes(S.drag.type))) return;
      e.preventDefault?.();
      pointerMoves.queue(e);
    }, { passive: false });

    function finishDimensionPointerDrag(e) {
      if (interruptedPress) return false;
      if (!(S.drag?.active && S.drag.type === 'dimension')) return false;
      e.preventDefault?.();
      if (e.type === 'pointercancel') pointerMoves.cancel();
      else pointerMoves.flush();
      releaseSvgPointerCapture(e.pointerId);
      if (handleDimensionPointerUp(e)) {
        setInteractionFlag('suppressPlanClick', true);
        return true;
      }
      return false;
    }

    window.addEventListener('pointerup', finishDimensionPointerDrag);
    window.addEventListener('pointercancel', finishDimensionPointerDrag);

    function finishTextAnnotationPointerDrag(e) {
      if (interruptedPress) return false;
      if (!(S.drag?.active && S.drag.type === 'textAnnotation')) return false;
      e.preventDefault?.();
      if (e.type === 'pointercancel') pointerMoves.cancel();
      else pointerMoves.flush();
      releaseSvgPointerCapture(e.pointerId);
      if (handleTextAnnotationPointerUp(e)) {
        setInteractionFlag('suppressPlanClick', true);
        return true;
      }
      return false;
    }

    window.addEventListener('pointerup', finishTextAnnotationPointerDrag);
    window.addEventListener('pointercancel', finishTextAnnotationPointerDrag);

    function finishAreaMeasurementVertexDrag(e) {
      if (interruptedPress) return false;
      if (!(S.drag?.active && S.drag.type === 'areaMeasurementVertex')) return false;
      e.preventDefault?.();
      if (e.type === 'pointercancel') pointerMoves.cancel();
      else pointerMoves.flush();
      releaseSvgPointerCapture(e.pointerId);
      if (handleAreaMeasurementPointerUp(e)) {
        setInteractionFlag('suppressPlanClick', true);
        return true;
      }
      return false;
    }

    window.addEventListener('pointerup', finishAreaMeasurementVertexDrag);
    window.addEventListener('pointercancel', finishAreaMeasurementVertexDrag);

    function handlePlanPointerUp(e) {
      const focusLoss = e.type === 'blur';
      if (interruptedPress && !focusLoss) return;
      // Die Dreh-Griffe (Gruppe/Mehrfachauswahl) verwalten sich vollstaendig
      // ueber ihre EIGENEN, gestenweise gebundenen pointerup-Listener in
      // selection_controller.js. Bei Touch feuert (ueber CDP jedenfalls)
      // trotz preventDefault() auf dem pointerdown noch ein kompatibles
      // "mouseup" -- landet dieses hier VOR jenem spezifischen Listener,
      // setzt der pauschale Reset unten S.drag.type/.active zurueck, bevor
      // der Dreh-Abschluss ueberhaupt den Zieltyp pruefen kann. Ergebnis:
      // jede zweite Drehgeste fand kein aktives S.drag mehr vor und der
      // naechste Dreh-Griff griff auf laengst veraltete Winkel-Daten zurueck
      // (Akkumulations-Bug). Deshalb hier komplett aussteigen und dem
      // eigenen Listener die Kontrolle ueberlassen.
      if (['coreGroupRotatePending', 'coreGroupRotate', 'multiSelectionRotatePending', 'multiSelectionRotate'].includes(S.drag?.type)) {
        return;
      }
      const profile = appPerfTimer('window mouseup');
      try {
        // Commit the newest actual move before ending its drag. Replacing it
        // with mouseup would lose buttons=1 used by clipboard-pan detection.
        if (focusLoss) pointerMoves.cancel();
        else pointerMoves.flush();
        clearAllInteractionTimers();
        clearAllInteractionFlagsSoon();
        appPerfStep(profile, 'timers');
        if (ClipboardController.handleClipboardPastePointerUp(e, { currentView: S.project.currentView })) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        if (handleDimensionPointerUp(e)) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        if (handleTextAnnotationPointerUp(e)) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        if (handleAreaMeasurementPointerUp(e)) {
          setInteractionFlag('suppressPlanClick', true);
          return;
        }
        if (handleToolPointerUp(e, { currentView: S.project.currentView })) {
          return;
        }
        appPerfStep(profile, 'tool-pointerup');
        if (S.drag.active && S.drag.type === 'planBoxSelect') {
          SelectionController.finishPlanBoxSelection();
        }
        if (S.drag.active && S.drag.type === 'floorplan') {
          if (FloorplanController.finishFloorplanDrag()) {
            setInteractionFlag('suppressPlanClick', true);
          }
        }
        if (S.drag.active && S.drag.type === 'multiSelection') {
          SelectionController.clearMultiSelectionDragPreview(S.drag);
          const rect = svg.getBoundingClientRect();
          const world = focusLoss ? null : screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
          const lastDelta = lastMultiSelectionDelta?.drag === S.drag ? lastMultiSelectionDelta : null;
          const dx = focusLoss ? lastDelta?.dx ?? (Number(S.drag.lastDx) || 0) : world.x - S.drag.startMouse.x;
          const dz = focusLoss ? lastDelta?.dz ?? (Number(S.drag.lastDz) || 0) : world.z - S.drag.startMouse.z;
          if (Math.hypot(dx, dz) > 0.001) {
            S.drag.hasMoved = true;
            SelectionController.applyMultiSelectionMoveDelta(S.drag, dx, dz);
          }
          appPerfStep(profile, 'multi-move-apply');
          SelectionController.syncSelectionAfterMultiMove(S.drag);
          AppController.requestAppRefresh(false, 'multi-selection-drop');
          if (S.drag.hasMoved) {
            setInteractionFlag('suppressPlanClick', true);
          }
        }
        appPerfStep(profile, 'drag-finalize');
        if (S.drag.active) setStatus('Bereit');
        S.drag.active = false;
        if (!SystemRegistry.dragTypeSurvivesPointerUp(S.drag.type)) S.drag.type = null;
        S.drag.snapCandidate = null;
        S.drag.crosslatBridgeCandidate = null;
      } finally {
        pressContext = null;
        lastMultiSelectionDelta = null;
        appPerfEnd(profile);
      }
    }

    // Preventing pointerdown for paste suppresses compatibility mouse events.
    // Finish that gesture on pointerup as well; the later mouseup sees no gesture.
    window.addEventListener('pointerup', event => {
      if (!ClipboardController.currentClipboardPastePending()?.gesture?.active) return;
      coreOverlayPointerDownSuppression = null;
      pointerMoves.flush();
      if (ClipboardController.handleClipboardPastePointerUp(event, { currentView: S.project.currentView })) {
        setInteractionFlag('suppressPlanClick', true);
      }
    });
    window.addEventListener('pointercancel', () => {
      if (ClipboardController.currentClipboardPastePending()?.gesture?.active) {
        coreOverlayPointerDownSuppression = null;
        ClipboardController.clearClipboardPastePending();
      }
    });
    window.addEventListener('mouseup', handlePlanPointerUp);

    function finishPlanInteractionOnFocusLoss(event) {
      if (event.target !== window) return;
      // Commit only work already presented by the last processed frame. Blur
      // is not a pointer release and must never infer a new cursor position.
      pointerMoves.cancel();
      interruptedPress = true;
      clearAllInteractionTimers();
      clearInteractionFlag('spaceDown');
      lastMiddleMouseDownAt = 0;
      coreOverlayPointerDownSuppression = null;
      releaseSvgPointerCapture(capturedPointerId);
      const contextCurrent = pressContextIsCurrent();
      try {
        SelectionController.finishPlanRotationOnFocusLoss();
        if (ClipboardController.currentClipboardPastePending()?.ownerContext) ClipboardController.clearClipboardPastePending();
        // The press is transient even if navigation has replaced its document.
        if (ClipboardController.currentClipboardPastePending()?.gesture?.active) {
          ClipboardController.handleClipboardPastePointerUp(event, { currentView: S.project?.currentView });
        }
        if (!contextCurrent) {
          // A replaced project/sheet must not receive the old gesture's commit.
          if (S.drag?.active) S.drag = { active: false, type: null };
          return;
        }
        if (S.drag?.active) {
          handlePlanPointerUp(event);
        }
      } finally {
        pressContext = null;
        lastMultiSelectionDelta = null;
        hidePlanContextMenu();
        if (S.project?.currentView === 'plan') AppController.requestPlanRender('plan-focus-loss');
      }
    }

    svg.addEventListener('wheel', e => {
      if (S.project.currentView !== 'plan') return;
      pointerMoves.flush();
      hidePlanContextMenu();
      const profile = appPerfTimer('svg wheel');
      try {
        e.preventDefault();
        const before = screenToWorld(e.offsetX, e.offsetY);
        const factor = wheelZoomFactor(e);
        S.camera2D.scale = clamp(S.camera2D.scale * factor, PLAN_MIN_SCALE, planSurfaceMaxScale(PLAN_MAX_SCALE));
        const after = screenToWorld(e.offsetX, e.offsetY);
        S.camera2D.x += before.x - after.x;
        S.camera2D.z += before.z - after.z;
        AppController.startPlanNavigationLod('wheel');
        AppController.requestPlanRender('wheel', { kind: 'navigation' });
        appPerfStep(profile, 'request-plan-render');
      } finally {
        appPerfEnd(profile);
      }
    }, { passive:false });
    svg.addEventListener('auxclick', e => {
      if (e.button !== 1) return;
      e.preventDefault();
    });

    svg.addEventListener('contextmenu', e => {
      pointerMoves.cancel();
      e.preventDefault();
      e.stopPropagation();
      if (S.project.currentView !== 'plan') return;
      showPlanContextMenu(e);
    });

    window.addEventListener('keydown', e => {
      if (e.key === 'Escape' && hidePlanContextMenu()) {
        e.preventDefault();
        return;
      }
      if (!EditCommandController.shouldHandleGlobalKeyDown(e)) return;
      if (e.key === 'Escape') pointerMoves.cancel();
      else pointerMoves.flush();
      if (handleGlobalObstacleLengthInputKey(e)) return;
      if (e.key === 'Escape' && ClipboardController.clearClipboardPastePending()) {
        e.preventDefault();
        return;
      }
      if (EditCommandController.handleEscapeKey(e)) return;
      if (EditCommandController.handleUndoRedoKey(e)) return;
      if (ClipboardController.handleClipboardKey(e)) return;
      if (EditCommandController.handleDeleteKey(e)) return;
      if (EditCommandController.handleRotateKey(e)) return;
      if (EditCommandController.handleArrowNudge(e)) return;
      if (e.code === 'Space') setInteractionFlag('spaceDown', true);
    });
    window.addEventListener('copy', e => {
      if (EditCommandController.isModalOpen() || EditCommandController.isTypingTarget(e.target) || !SelectionController.hasGlobalSelection()) return;
      ClipboardController.copyToClipboardEvent(e);
    });
    window.addEventListener('paste', e => {
      if (EditCommandController.isModalOpen() || EditCommandController.isTypingTarget(e.target)) return;
      ClipboardController.pasteFromClipboardEvent(e);
    });
    window.addEventListener('keyup', e => { if (e.code === 'Space') setInteractionFlag('spaceDown', false); });

    bindSvgTouchInteractions(svg);
}

// Phase 4 (project_responsive_mobile_20260731): additive Touch-Steuerung.
// #planSvg hat touch-action:none (styles.css), damit der Browser bei
// Beruehrungen nicht selbst die Seite verschiebt/zoomt -- das unterbindet
// aber gleichzeitig die vom Browser sonst automatisch erzeugten
// synthetischen Maus-Events (mousedown/mousemove/mouseup/click), auf denen
// das gesamte obenstehende Maus-Modell beruht. Statt dieses Modell
// (Objekte greifen/verschieben, Mehrfachauswahl-Drag,
// Platzierungswerkzeuge, Klick-Trefferpruefung) fuer Touch zu duplizieren,
// werden hier passende MouseEvents SELBST synthetisiert und ganz normal
// ueber svg.dispatchEvent(...)/window.dispatchEvent(...) verschickt --
// die bestehenden, oben unveraendert gebliebenen Handler greifen dadurch
// exakt wie bei echter Maus. Echten neuen Code braucht nur, was bei Maus
// kein Vorbild hat: Ein-Finger-Ziehen auf leerer Flaeche/Objekten ohne
// eigenes Mousedown-Drag wird zum Verschieben (statt Auswahlrahmen wie bei
// Maus), ein langer Druck startet stattdessen den Auswahlrahmen, und
// Zwei-Finger-Gesten steuern Zoom+Verschieben gemeinsam.
function bindSvgTouchInteractions(svg) {
    const TOUCH_LONG_PRESS_MS = 450;
    const TOUCH_MOVE_THRESHOLD_PX = 10;
    // S.drag-Typen, die NUR unser eigener Touch-Bridge auf Empty-Space
    // startet (Verschieben per Finger, Auswahlrahmen per langem Druck) --
    // siehe cancelSingleFingerGesture() unten.
    const OWN_DRAG_TYPES = ['pan', 'planBoxSelect'];

    // WICHTIG: Auf ECHTEN touchstart/touchmove/touchend/touchcancel-Events
    // aufzubauen (frueherer Ansatz) ist bei diesem Feature nicht robust:
    // Touch-Events sind laut Spec fuer die GESAMTE Geste an das
    // urspruengliche touchstart-Zielelement gebunden. Dreh-/Verschiebe-
    // Gesten loesen aber bei JEDEM Zwischenschritt einen Re-Render aus
    // (SVG-Kinder werden neu aufgebaut), wodurch das urspruengliche
    // Zielelement (z.B. der Dreh-Griff-Kreis) aus dem DOM verschwindet --
    // danach kommen bei manchen Geraeten/Browsern gar keine touchmove/
    // touchend-Events mehr an, das Element bleibt fuer immer "gedrueckt"
    // haengen (genau das vom Nutzer gemeldete Symptom). Empirisch mit
    // Playwright+CDP nachgewiesen: ein reiner Tipp (kein Re-Render
    // dazwischen) funktionierte, ein Dreh-Drag mit mehreren Zwischen-
    // Schritten nicht mehr. Pointer-Events sind dagegen bei jedem
    // move/up-Schritt frisch trefferGEPRUEFT (wie bei Maus) und ueberleben
    // das Verschwinden des urspruenglichen Elements -- deshalb hier
    // durchgehend pointerdown/pointermove/pointerup/pointercancel
    // (gefiltert auf pointerType==='touch') statt touchstart/-move/-end.

    // Manche Elemente wie direkte Dreh-Griffe verwalten Touch/Maus schon
    // komplett selbst ueber ECHTE
    // pointerdown-Listener DIREKT auf dem Griff-Element und schuetzen sich
    // vor unserem generischen Maus-Modell exakt so, wie sie sich schon
    // immer vor ECHTER Maus geschuetzt haben: mit einem eigenen
    // btn.addEventListener('mousedown', evt => evt.stopPropagation())
    // auf demselben Element. Damit dieser Schutz auch fuer unsere
    // SYNTHETISCHEN MouseEvents greift, muessen mousedown/click auf dem
    // ECHTEN Element unter dem Finger ausgeloest werden (elementFromPoint)
    // statt immer pauschal auf #planSvg -- sonst ist e.target IMMER svg
    // statt des Griffs, der Schutz laeuft ins Leere, und unser generisches
    // mousedown wuerde ueber die Koordinaten-Trefferpruefung (z.B. den
    // Auswahlrahmen-Fallback) einen ZWEITEN, konkurrierenden Drag auf
    // demselben S.drag-Objekt starten und den frisch gesetzten
    // Dreh-Zustand ueberschreiben.
    function resolveTouchTarget(point) {
      const el = document.elementFromPoint(point.clientX, point.clientY);
      return el && svg.contains(el) ? el : svg;
    }

    // Ruft ein Griff beim echten pointerdown
    // preventDefault() auf, unterdrueckt der Browser bei ECHTER Maus/Touch
    // dadurch automatisch das gesamte nachfolgende Kompatibilitaets-"click"
    // (Pointer-Events-Spec) -- ein reiner Tipp auf so einen Griff dreht
    // dann NUR einmal, ueber dessen eigenes pointerdown/pointerup. Da wir
    // click selbst synthetisieren (touch-action:none unterbindet auch
    // dessen automatische Erzeugung), muessen wir dasselbe Signal manuell
    // nachbilden -- sonst wuerde z.B. ein Tipp auf einen Dreh-Griff nach
    // dessen korrektem 90°-Dreh-Abschluss zusaetzlich unser eigenes
    // synthetisches "click" auf denselben Griff durchreichen, was ueber
    // dessen generische Klick-Weiche eine ZWEITE, ungewollte Drehung
    // ausloest (die Griffe nutzen dafuer jeweils eigene, modul-lokale
    // Unterdrueckungs-Flags statt des gemeinsamen suppressPlanClick).
    // Eigene, VOR-geschaltete Capture-Phase-Beobachtung noetig, weil ein
    // Griff mit stopPropagation() unseren EIGENEN (bubble-phasigen)
    // pointerdown-Listener auf svg weiter unten gar nie erreichen laesst.
    let lastTouchPointerDown = null;
    document.addEventListener('pointerdown', event => {
      if (event.pointerType === 'touch') lastTouchPointerDown = event;
    }, true);

    function dispatchSyntheticMouse(type, point) {
      const target = type === 'mouseup'
        ? window
        : (type === 'mousedown' || type === 'click') ? resolveTouchTarget(point) : svg;
      const evt = new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: point.clientX,
        clientY: point.clientY,
        screenX: point.screenX,
        screenY: point.screenY,
        button: 0,
        buttons: type === 'mouseup' ? 0 : 1
      });
      target.dispatchEvent(evt);
    }

    const activePointers = new Map();
    let pendingTouch = null;
    let longPressTimer = 0;
    let pinch = null;
    let gestureGeneration = 0;

    function clearLongPressTimer() {
      if (!longPressTimer) return;
      clearTimeout(longPressTimer);
      longPressTimer = 0;
    }

    window.addEventListener('blur', event => {
      if (event.target !== window) return;
      gestureGeneration += 1;
      clearLongPressTimer();
      activePointers.clear();
      pendingTouch = null;
      pinch = null;
      lastTouchPointerDown = null;
    });

    function pinchPoints() {
      return [...activePointers.values()];
    }

    function startPinch() {
      const [a, b] = pinchPoints();
      if (!a || !b) return;
      pinch = { distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) };
    }

    function updatePinch() {
      const [a, b] = pinchPoints();
      if (!a || !b || !pinch) return;
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const midX = (a.clientX + b.clientX) / 2;
      const midY = (a.clientY + b.clientY) / 2;
      const rect = svg.getBoundingClientRect();
      const before = screenToWorld(midX - rect.left, midY - rect.top);
      if (distance > 0 && pinch.distance > 0) {
        const factor = distance / pinch.distance;
        S.camera2D.scale = clamp(S.camera2D.scale * factor, PLAN_MIN_SCALE, planSurfaceMaxScale(PLAN_MAX_SCALE));
      }
      const after = screenToWorld(midX - rect.left, midY - rect.top);
      S.camera2D.x += before.x - after.x;
      S.camera2D.z += before.z - after.z;
      pinch.distance = distance;
      AppController.startPlanNavigationLod('touch-pinch');
      AppController.requestPlanRender('touch-pinch', { kind: 'navigation' });
    }

    function cancelSingleFingerGesture() {
      clearLongPressTimer();
      const [firstPoint] = pinchPoints();
      pendingTouch = null;
      // Nur EIGENE Gesten (Verschieben/Auswahlrahmen) beim Dazukommen eines
      // zweiten Fingers sauber abschliessen. Ist gerade etwas Fremdes aktiv
      // (z.B. ein Dreh-Griff), lassen wir es unangetastet weiterlaufen --
      // der zweite Finger startet dann trotzdem einen Pinch nebenher.
      if (firstPoint && S.drag?.active && OWN_DRAG_TYPES.includes(S.drag.type)) {
        dispatchSyntheticMouse('mouseup', firstPoint);
      }
    }

    // Capture-Phase (nicht Bubble): manche Systemelemente rufen auf ihrem
    // EIGENEN pointerdown
    // bedingungslos stopPropagation() auf, ohne preventDefault() -- bei
    // ECHTER Maus stoert das nicht, weil der Browser das kompatible
    // "mousedown" als SEPARATES Event erzeugt, das von jener
    // stopPropagation() unberuehrt bleibt und normal bis zu svg
    // durchbubbelt. Ein bubble-phasiger Listener hier wuerde fuer Touch
    // (wo WIR mousedown erst noch synthetisieren muessen) nie erreicht --
    // Andernfalls laesst sich das betroffene Systemobjekt per Touch nicht
    // verschieben, weil der Bridge-Code fuer diesen Finger nie laeuft. Capture
    // greift schon auf dem Weg ZUM Ziel, bevor dessen eigenes
    // stopPropagation() ueberhaupt zum Tragen kommt.
    svg.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch') return;
      if (S.project.currentView !== 'plan') return;
      activePointers.set(event.pointerId, event);
      if (activePointers.size >= 2) {
        event.preventDefault();
        cancelSingleFingerGesture();
        startPinch();
        return;
      }
      event.preventDefault();
      dispatchSyntheticMouse('mousedown', event);
      if (S.drag?.active && S.drag.type === 'planBoxSelect') {
        // Kein Maus-Aequivalent: ein Finger auf einer Flaeche ohne eigenes
        // Drag (leerer Bereich oder z.B. ein Hindernis, dessen Auswahl erst
        // beim Klick passiert) verschiebt auf Touch standardmaessig den
        // Plan statt einen Auswahlrahmen zu starten; der Rahmen kommt
        // stattdessen per langem Druck (siehe AskUserQuestion-Entscheid).
        S.drag.active = false;
        S.drag.type = null;
        // startPlanBoxSelection() (oben via mousedown durchgereicht) hat
        // bereits "Auswahlrahmen ziehen" gesetzt, obwohl noch gar nichts
        // aktiv ist -- zuruecksetzen, sonst bleibt die Statuszeile bis zum
        // langen Druck (oder nach einem reinen Tipp fuer immer) falsch
        // stehen. startPan()/der Timer setzen bei Bedarf gleich wieder um.
        setStatus('Bereit');
        pendingTouch = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY };
        longPressTimer = setTimeout(() => {
          if (!pendingTouch || pendingTouch.pointerId !== event.pointerId) return;
          pendingTouch = null;
          SelectionController.startPlanBoxSelection({
            clientX: event.clientX,
            clientY: event.clientY,
            preventDefault: () => undefined
          });
          setInteractionFlag('suppressPlanClick', true);
        }, TOUCH_LONG_PRESS_MS);
      }
    }, { capture: true });

    window.addEventListener('pointermove', event => {
      if (event.pointerType !== 'touch') return;
      if (S.project.currentView !== 'plan') return;
      if (!activePointers.has(event.pointerId)) return;
      activePointers.set(event.pointerId, event);
      if (pinch) {
        event.preventDefault();
        updatePinch();
        return;
      }
      if (pendingTouch && event.pointerId === pendingTouch.pointerId) {
        const moved = Math.hypot(event.clientX - pendingTouch.startX, event.clientY - pendingTouch.startY);
        if (moved <= TOUCH_MOVE_THRESHOLD_PX) return;
        clearLongPressTimer();
        pendingTouch = null;
        event.preventDefault();
        ViewportController.startPan({
          clientX: event.clientX,
          clientY: event.clientY,
          preventDefault: () => undefined
        });
      }
      if (S.drag?.active) event.preventDefault();
      dispatchSyntheticMouse('mousemove', event);
    }, { passive: false });

    function handlePointerEnd(event) {
      if (event.pointerType !== 'touch') return;
      if (!activePointers.has(event.pointerId)) return;
      activePointers.delete(event.pointerId);
      if (pinch) {
        if (activePointers.size < 2) pinch = null;
        return;
      }
      if (pendingTouch && event.pointerId === pendingTouch.pointerId) {
        clearLongPressTimer();
        pendingTouch = null;
      }
      // Microtask-Verzoegerung ist hier notwendig, nicht kosmetisch:
      // manche Mechanismen (z.B. ein generischer Dreh-Griff in
      // selection_controller.js) binden ihren EIGENEN echten
      // pointerup-Listener erst beim Start ihrer Geste (Druckbeginn),
      // waehrend unser Listener hier schon beim App-Start einmalig
      // registriert wird. Fuer mehrere Listener desselben Typs auf
      // demselben Ziel gilt Registrierungsreihenfolge -- unserer liefe
      // also IMMER zuerst. Wuerden wir hier sofort synchron ein
      // mouseup/click verschicken, wuerde dessen generische
      // Aufraeum-Logik S.drag zuruecksetzen, BEVOR der eigentliche
      // (spaeter registrierte) Abschluss-Listener drankommt -- der findet
      // dann nur noch einen bereits genullten Zustand vor, seine eigene
      // Klick-Unterdrueckung greift nie, und ein nachfolgender
      // synthetischer Klick loest darueber faelschlich eine ZWEITE Aktion
      // aus. Ein Microtask laeuft garantiert erst
      // NACH allen synchronen pointerup-Listenern dieses Events, egal in
      // welcher Reihenfolge sie registriert wurden.
      const endGeneration = gestureGeneration;
      queueMicrotask(() => {
        if (endGeneration !== gestureGeneration) return;
        dispatchSyntheticMouse('mouseup', event);
        const nativeClickSuppressed = lastTouchPointerDown?.defaultPrevented === true;
        lastTouchPointerDown = null;
        // clearInteractionFlag statt getInteractionFlag: das Flag muss so
        // oder so zurueckgesetzt werden (sonst bliebe es haengen und
        // unterdrueckt faelschlich den naechsten Tipp) -- die echte
        // svg-click-Weiche macht es bei Maus genauso.
        const appSuppressed = clearInteractionFlag('suppressPlanClick');
        if (!appSuppressed && !nativeClickSuppressed) dispatchSyntheticMouse('click', event);
      });
    }

    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
}

export {
  bindSvgInteractions,
  wheelZoomFactor,
  preferredHitSystemFromTarget,
  hitTestActiveDrawingSystem
};
